import {
  ChakraBox as Box,
  ChakraFlex as Flex,
  ChakraGrid as Grid,
  ChakraHeading as Heading,
  ChakraModalCloseButton as ModalCloseButton,
  ChakraText as Text,
} from "@airhelp/react-ui-components";
import { useLanguage, useTranslations } from "contexts/LocalizationContext";
import { IconLink } from "modules/common/components/IconLink";
import PlanBox from "modules/common/components/PlanBox";
import { Stars } from "modules/common/components/Trustpilot/ReviewCard/Stars";
import { renderDynamicComponent } from "modules/storyblok/DynamicComponent";
import { AirhelpPlusItalicsLogo } from "styles/logos";
import type { SbEditableComponentProps } from "types/storyblok";
import { getPageUrl } from "utils/getPageUrl";
import usePricingPlanDetailsConfig from "./AhPlusModal.config";
import type { AhPlusModalProps } from "./AhPlusModal.d";

export const AhPlusModal = ({
  heading,
  review,
  linkText,
  reviewer,
  ahPlusPricingPlans,
}: SbEditableComponentProps<AhPlusModalProps>) => {
  const language = useLanguage();
  const translations = useTranslations();
  const { essential: essentialPlan, complete: completePlan } = usePricingPlanDetailsConfig();
  // TODO | Remove when all the data is migrated
  const isNewModal = heading && review && linkText && reviewer && Boolean(ahPlusPricingPlans);

  const templateAreasStyles = isNewModal
    ? {
        base: "'header' 'ahPlusPricingPlans' 'description'",
        lg: "'header header' 'ahPlusPricingPlans ahPlusPricingPlans' 'description .'",
        xl: "'header ahPlusPricingPlans ahPlusPricingPlans' ' description ahPlusPricingPlans ahPlusPricingPlans'",
      }
    : {
        base: "'header' 'essential' 'complete' 'description'",
        lg: "'header header' 'essential complete' 'description .'",
        xl: "'header essential complete' 'description essential complete'",
      };

  return (
    <Flex bg="primary.900" minHeight={{ base: "full", xl: "calc(100vh - 32px)" }} flexDir="column" justify="center">
      <ModalCloseButton size="lg" zIndex={2} _hover={{}} _active={{}} />
      <Grid
        justifyContent="center"
        justifyItems="center"
        gap={7}
        templateColumns={{ base: "1fr", lg: "repeat(2, minmax(auto, 400px))", xl: "repeat(3, minmax(auto, 400px))" }}
        templateRows={{ base: "repeat(4, auto)", lg: "repeat(3, auto)", xl: "repeat(3, auto)" }}
        templateAreas={templateAreasStyles}
        px={{ base: 4, md: 10 }}
        py={10}
        pt={{ base: 14, md: 20 }}
        pos="relative"
      >
        <Box color="white" gridArea="header">
          <AirhelpPlusItalicsLogo w="140px" h="auto" mb={4} display={{ base: "none", xl: "initial" }} />
          <Heading
            color="inherit"
            variant="h2"
            as="h2"
            mx={{ base: 0, md: 10, xl: 0 }}
            textAlign={{ base: "center", xl: "left" }}
          >
            {heading || translations("choose_plan_that_fits_your_needs")}
          </Heading>
        </Box>
        <Flex gridArea="description" justify="flex-end" flexDir="column" color="white" mt={{ base: 6, xl: 0 }}>
          <Box mb={{ base: 20, xl: 4 }} minH={{ xl: "250px" }}>
            <Stars />
            <Text color="inherit" mt={6} mb={4} fontSize="lg">
              {review || translations("airhelp_plus_review")}
            </Text>
            <Text color="inherit" fontWeight="bold" fontSize="md">
              {reviewer || translations("airhelp_plus_reviewer")}
            </Text>
          </Box>
          <IconLink
            color="white"
            textDecor="underline"
            _visited={{}}
            _hover={{
              textDecor: "underline",
              color: "white",
            }}
            href={getPageUrl(language, "airhelpPlus")}
            text={linkText || translations("learn_more_about_ah_plus")}
            title={linkText || translations("learn_more_about_ah_plus")}
            prefetch
          />
        </Flex>
        {isNewModal ? (
          <Box gridArea="ahPlusPricingPlans">{renderDynamicComponent(ahPlusPricingPlans)}</Box>
        ) : (
          <>
            {/* @ts-ignore */}
            <PlanBox gridArea="essential" planDetails={essentialPlan} />
            {/* @ts-ignore */}
            <PlanBox gridArea="complete" planDetails={completePlan} />
          </>
        )}
      </Grid>
    </Flex>
  );
};
